import React, { useCallback, useEffect, useRef, useState } from "react";
import EdiInboxFilters from "../components/edi/EdiInboxFilters";
import EdiMessageDrawer from "../components/edi/EdiMessageDrawer";
import EdiMessageTable from "../components/edi/EdiMessageTable";
import { usePermissions } from "../utils/permissions";
import { getCurrentUser } from "../utils/auth";
import { downloadEdi, ediJson } from "../utils/ediApi";
import { buildEdiMessageQuery, DEFAULT_EDI_FILTERS, ediReadErrorMessage } from "../utils/ediPresentation";

const freshFilters = () => ({ ...DEFAULT_EDI_FILTERS });

export default function EdiInbox() {
  const user = getCurrentUser();
  const { getLevel } = usePermissions();
  const full = getLevel(user?.role, "edi-inbox") === "Full";
  const [filters, setFilters] = useState(freshFilters);
  const [applied, setApplied] = useState(freshFilters);
  const [data, setData] = useState({ items: [], total: 0 });
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [detailHistory, setDetailHistory] = useState([]);
  const mountedRef = useRef(true);
  const listRequestIdRef = useRef(0);
  const detailRequestIdRef = useRef(0);
  const limit = 50;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      listRequestIdRef.current += 1;
      detailRequestIdRef.current += 1;
    };
  }, []);

  const loadList = useCallback(async (requestedFilters, requestedOffset) => {
    const requestId = ++listRequestIdRef.current;
    setLoading(true);
    setError("");
    try {
      const query = buildEdiMessageQuery(requestedFilters, requestedOffset, limit);
      const value = await ediJson(`/api/edi/messages?${query}`);
      if (!value || !Array.isArray(value.items) || typeof value.total !== "number") {
        throw new Error("The EDI service returned an invalid message list.");
      }
      if (!mountedRef.current || requestId !== listRequestIdRef.current) return;
      setData(value);
    } catch (requestError) {
      if (mountedRef.current && requestId === listRequestIdRef.current) {
        setError(ediReadErrorMessage(requestError));
      }
    } finally {
      if (mountedRef.current && requestId === listRequestIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadList(applied, offset);
  }, [applied, loadList, offset]);

  const loadDetail = useCallback(async (id) => {
    const requestId = ++detailRequestIdRef.current;
    setDetailLoading(true);
    setError("");
    try {
      const value = await ediJson(`/api/edi/messages/${id}`);
      if (!value?.message) throw new Error("The EDI service returned invalid message details.");
      if (!mountedRef.current || requestId !== detailRequestIdRef.current) return false;
      setDetail(value);
      return true;
    } catch (requestError) {
      if (mountedRef.current && requestId === detailRequestIdRef.current) {
        setError(ediReadErrorMessage(requestError, "Unable to load EDI message details."));
      }
      return false;
    } finally {
      if (mountedRef.current && requestId === detailRequestIdRef.current) setDetailLoading(false);
    }
  }, []);

  const openFromTable = (id) => {
    setDetailHistory([]);
    loadDetail(id);
  };

  const navigateToLinkedMessage = (id) => {
    const currentId = detail?.message?.id;
    if (id == null || id === currentId) return;
    setDetailHistory((previous) => {
      const existingIndex = previous.lastIndexOf(id);
      if (existingIndex >= 0) return previous.slice(0, existingIndex);
      if (previous[previous.length - 1] === currentId) return previous;
      return currentId == null ? previous : [...previous, currentId];
    });
    loadDetail(id);
  };

  const goBack = () => {
    if (!detailHistory.length) return;
    const id = detailHistory[detailHistory.length - 1];
    setDetailHistory((previous) => previous.slice(0, -1));
    loadDetail(id);
  };

  const closeDetail = () => {
    detailRequestIdRef.current += 1;
    setDetailLoading(false);
    setDetail(null);
    setDetailHistory([]);
  };

  const downloadRaw = async () => {
    if (!full || !detail?.message?.id) return;
    setError("");
    try {
      await downloadEdi(`/api/edi/messages/${detail.message.id}/raw`, `edi-message-${detail.message.id}.x12`);
    } catch {
      setError("Unable to download raw X12.");
    }
  };

  const changeFilter = (key, value) => setFilters((previous) => ({ ...previous, [key]: value }));
  const applyFilters = () => {
    setOffset(0);
    setApplied({ ...filters });
  };
  const clearFilters = () => {
    const defaults = freshFilters();
    setFilters(defaults);
    setOffset(0);
    setApplied({ ...defaults });
  };

  return <div className="space-y-4 p-4"><header className="flex flex-wrap items-start justify-between gap-4"><div><h1 className="text-2xl font-bold">EDI Inbox</h1><p className="text-sm text-slate-600">Read-only EDI history, transport metadata, and acknowledgement relationships.</p></div><button type="button" disabled={loading} className="rounded border px-3 py-2 disabled:opacity-50" onClick={() => loadList(applied, offset)}>Refresh Inbox</button></header>
    <EdiInboxFilters filters={filters} onChange={changeFilter} onApply={applyFilters} onClear={clearFilters}/>
    {error && <div role="alert" className="rounded bg-red-50 p-3 text-red-700">{error}</div>}
    {loading && <div role="status">Loading EDI messages...</div>}
    {!loading && data.items.length === 0 && <div>No EDI messages found for the applied filters.</div>}
    {!loading && data.items.length > 0 && <EdiMessageTable rows={data.items} onView={openFromTable}/>}
    <nav aria-label="EDI inbox pagination" className="flex items-center gap-3"><button type="button" className="rounded border px-3 py-1 disabled:cursor-not-allowed disabled:opacity-40" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(0, offset - limit))}>Previous</button><span>{data.total ? offset + 1 : 0}{"\u2013"}{Math.min(offset + limit, data.total)} of {data.total}</span><button type="button" className="rounded border px-3 py-1 disabled:cursor-not-allowed disabled:opacity-40" disabled={offset + limit >= data.total || loading} onClick={() => setOffset(offset + limit)}>Next</button></nav>
    <EdiMessageDrawer detail={detail} loading={detailLoading} full={full} canGoBack={detailHistory.length > 0} onBack={goBack} onClose={closeDetail} onNavigate={navigateToLinkedMessage} onDownload={downloadRaw}/>
  </div>;
}
